/* Copyright (c) 2020-present CloudIO Inc */

import { makeStyles } from '@mui/styles';
import cx from 'clsx';
import React, { memo } from 'react';
import { ItemID, AnyColumn } from 'componentregistry';

import { Theme, Direction } from '@mui/material';
import Cells from './Cells';
import { Cell, CellComponentProps, DataContext } from './types';
import zIndex from 'constants/ZIndex';
import { useGridBounds } from '../Context';
import { useSelector } from '@legendapp/state/react';

const useStyles = makeStyles((theme: Theme) => ({
  gridHeader: {
    position: 'absolute',
    overflow: 'hidden',
    top: 0,
    backgroundColor: theme.palette.background.paper,
  },
  gridHeaderCenter: {
    zIndex: zIndex.GridHeader - 1,
  },
  gridHeaderLeft: {
    zIndex: zIndex.GridHeader,
  },
  gridHeaderRight: {
    zIndex: zIndex.GridHeader,
  },
}));

interface Props {
  CellComponent: React.FunctionComponent<CellComponentProps>;
  appUid: string;
  roleUid: string;
  columnLefts: number[];
  columnWidths: number[];
  columns: AnyColumn[];
  custom: boolean;
  dataContext: DataContext;
  direction?: Direction;
  fixedLeftColumns: number;
  fixedRightColumns: number;
  headerRows: number;
  innerWidth: number;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  pageId: ItemID;
  resetScroll: () => void;
  rowHeight: number;
  showColumnLines: boolean;
  showRowLines: boolean;
  totalColumns: number;
  vertialScrollbarSize: number;
  totalRows: number;
}

function GridFilter(props: Props): React.ReactElement {
  const {
    CellComponent,
    appUid,
    roleUid,
    columnLefts,
    columnWidths,
    columns,
    custom,
    dataContext,
    direction,
    fixedLeftColumns,
    fixedRightColumns,
    headerRows,
    innerWidth,
    onClick,
    onKeyDown,
    pageId,
    resetScroll,
    rowHeight,
    showColumnLines,
    showRowLines,
    totalColumns,
    vertialScrollbarSize,
    totalRows,
  } = props;
  const classes = useStyles();
  const headerHeight = headerRows ? (headerRows - 1) * rowHeight : 0;

  const leftFixedWidth = fixedLeftColumns
    ? columnLefts[fixedLeftColumns - 1] + columnWidths[fixedLeftColumns - 1]
    : 0;
  const rightFixedWidth = fixedRightColumns
    ? innerWidth - columnLefts[totalColumns - fixedRightColumns]
    : 0;

  const gridBoundsSignal = useGridBounds();
  const { left, startColumn, endColumn, showShadow } = useSelector(() => {
    const { left: _left, top: _top } = gridBoundsSignal.pos.get();
    const column = gridBoundsSignal.bound.column.get();

    return {
      left: _left,
      startColumn: column.start,
      endColumn: column.end,
      showShadow: _top > 5,
    };
  });

  return (
    <>
      {fixedLeftColumns > 0 ? (
        <div
          className={cx(
            'io-grid-header-filter-left',
            classes.gridHeader,
            classes.gridHeaderLeft,
            showShadow ? 'io-grid-header-left-fixed-shadow' : null,
          )}
          style={{
            height: rowHeight,
            width: leftFixedWidth,
            top: headerHeight,
            [direction === 'rtl' ? 'right' : 'left']: 0,
          }}
        >
          <Cells
            CellComponent={CellComponent}
            appUid={appUid}
            roleUid={roleUid}
            columnLefts={columnLefts}
            columnWidths={columnWidths}
            columns={columns}
            custom={custom}
            dataContext={dataContext}
            direction={direction}
            endColumn={fixedLeftColumns - 1}
            endRow={headerRows - 1}
            fixedLeftColumns={fixedLeftColumns}
            headerRows={headerRows}
            onClick={onClick}
            onKeyDown={onKeyDown}
            pageId={pageId}
            region="header-left"
            resetScroll={resetScroll}
            rowHeight={rowHeight}
            showColumnLines={showColumnLines}
            showRowLines={showRowLines}
            startColumn={0}
            startRow={headerRows - 1}
            actualEndRow={headerRows - 1}
            actualStartRow={headerRows - 1}
            totalRows={totalRows}
          />
        </div>
      ) : null}
      <div
        className={cx(
          'io-grid-header-filter-main',
          classes.gridHeader,
          classes.gridHeaderCenter,
          showShadow ? 'io-grid-header-shadow' : null,
        )}
        style={{
          height: rowHeight,
          width: innerWidth,
          [direction === 'rtl' ? 'right' : 'left']: leftFixedWidth + -1 * left,
          top: headerHeight,
        }}
      >
        <Cells
          CellComponent={CellComponent}
          appUid={appUid}
          roleUid={roleUid}
          columnLefts={columnLefts}
          columnWidths={columnWidths}
          columns={columns}
          custom={custom}
          dataContext={dataContext}
          direction={direction}
          endColumn={endColumn}
          endRow={headerRows - 1}
          fixedLeftColumns={fixedLeftColumns}
          headerRows={headerRows}
          onClick={onClick}
          onKeyDown={onKeyDown}
          pageId={pageId}
          region="header-center"
          resetScroll={resetScroll}
          rowHeight={rowHeight}
          showColumnLines={showColumnLines}
          showRowLines={showRowLines}
          startColumn={startColumn}
          startRow={headerRows - 1}
          actualEndRow={headerRows - 1}
          actualStartRow={headerRows - 1}
          totalRows={totalRows}
        />
      </div>
      {fixedRightColumns > 0 ? (
        <div
          className={cx(
            'io-grid-header-filter-right',
            classes.gridHeader,
            classes.gridHeaderRight,
            showShadow ? 'io-grid-header-right-fixed-shadow' : null,
          )}
          style={{
            height: rowHeight,
            top: headerHeight,
            width: rightFixedWidth + vertialScrollbarSize,
            [direction === 'rtl' ? 'left' : 'right']: 0,
          }}
        >
          <Cells
            CellComponent={CellComponent}
            appUid={appUid}
            roleUid={roleUid}
            columnWidths={columnWidths}
            columns={columns}
            custom={custom}
            dataContext={dataContext}
            direction={direction}
            endColumn={totalColumns - 1}
            endRow={headerRows - 1}
            fixedLeftColumns={fixedLeftColumns}
            headerRows={headerRows}
            onClick={onClick}
            onKeyDown={onKeyDown}
            pageId={pageId}
            region="header-right"
            resetScroll={resetScroll}
            rowHeight={rowHeight}
            showColumnLines={showColumnLines}
            showRowLines={showRowLines}
            startColumn={totalColumns - fixedRightColumns}
            startRow={headerRows - 1}
            actualEndRow={headerRows - 1}
            actualStartRow={headerRows - 1}
            totalRows={totalRows}
          />
        </div>
      ) : null}
    </>
  );
}

export default memo<Props>(GridFilter);
